/**
 * QR Code URL Parser
 *
 * Parses EVee Buddy QR code URLs to extract navigation information.
 * The app-URL patterns are domain-agnostic - they only care about the URL path.
 *
 * Supported URL formats:
 * - Location:      https://{any-domain}/app/location/{locationId}
 * - ChargePoint:   https://{any-domain}/app/location/{locationId}/chargepoint/{chargePointId}
 * - Guest charger: https://{guest-host}/{chargerId} — the physical charger sticker.
 *   {chargerId} is the human-readable CSMS ID (e.g. EVBDY003), not a UUID. Guest
 *   URLs are only recognized on the known guest hosts so arbitrary single-segment
 *   URLs don't get treated as charger IDs.
 *
 * A ?gun= param names the connector the sticker is physically attached to, and is
 * carried through so the app can open that gun rather than guessing at one. The
 * guest app has always honoured it (guest-app ChargerPage "gun mode").
 */

export type QRParseResult =
  | { type: 'location'; locationId: string }
  | { type: 'chargepoint'; locationId: string; chargePointId: string; gun?: string }
  | { type: 'guest-charger'; chargerId: string; gun?: string };

export const GUEST_QR_HOSTS = ['go.eveebuddy.com', 'go.poweresh.online'];

/**
 * Parse a QR code URL and extract navigation information.
 *
 * @param url - The scanned QR code URL
 * @returns QRParseResult if valid, null if not recognized
 *
 * @example
 * // Location URL
 * parseQRCode('https://app.eveebuddy.com/app/location/loc123')
 * // Returns: { type: 'location', locationId: 'loc123' }
 *
 * @example
 * // ChargePoint URL
 * parseQRCode('https://app.eveebuddy.com/app/location/loc123/chargepoint/cp456')
 * // Returns: { type: 'chargepoint', locationId: 'loc123', chargePointId: 'cp456' }
 *
 * @example
 * // Guest sticker naming the gun it is glued beside
 * parseQRCode('https://go.eveebuddy.com/EVBDY003?gun=2')
 * // Returns: { type: 'guest-charger', chargerId: 'EVBDY003', gun: '2' }
 */
export function parseQRCode(url: string): QRParseResult | null {
  try {
    // Handle URLs without protocol (just in case)
    const urlToParse = url.includes('://') ? url : `https://${url}`;
    const parsed = new URL(urlToParse);
    const pathname = parsed.pathname;
    // Absent on most stickers; the key is omitted rather than set to undefined so
    // results stay comparable with toStrictEqual.
    const gunParam = parsed.searchParams.get('gun');
    const gun = gunParam ? { gun: gunParam } : {};

    // Match: /app/location/{locationId}/chargepoint/{chargePointId}
    const cpMatch = pathname.match(
      /\/app\/location\/([^/]+)\/chargepoint\/([^/]+)/
    );
    if (cpMatch) {
      return {
        type: 'chargepoint',
        locationId: cpMatch[1],
        chargePointId: cpMatch[2],
        ...gun,
      };
    }

    // Match: /app/location/{locationId}
    const locMatch = pathname.match(/\/app\/location\/([^/]+)$/);
    if (locMatch) {
      return {
        type: 'location',
        locationId: locMatch[1],
      };
    }

    // Guest charger sticker: single path segment on a known guest host
    if (GUEST_QR_HOSTS.includes(parsed.hostname.toLowerCase())) {
      const guestMatch = pathname.match(/^\/([^/]+)\/?$/);
      if (guestMatch) {
        return {
          type: 'guest-charger',
          chargerId: decodeURIComponent(guestMatch[1]),
          ...gun,
        };
      }
    }

    return null; // No pattern matched
  } catch {
    return null; // Invalid URL
  }
}

/**
 * Check if a string looks like a valid URL that could be a QR code.
 * Used for basic validation before parsing.
 */
export function isValidQRUrl(text: string): boolean {
  // Basic check - must contain a protocol or look like a domain
  return (
    text.includes('://') ||
    text.includes('.com') ||
    text.includes('.io') ||
    text.includes('.in') ||
    text.includes('.online') ||
    text.includes('/app/')
  );
}
